/* Per-optimization glyphs for the Agent-configuration cards (Figma 284:3227,
 * "ItemIcon" nodes) — 20×20 currentColor strokes so they tint with the icon
 * tile's on/off ground. Path data is the design's own vectors; `cleanup` and
 * `shrinking` ship as bitmaps in the file, so those two are redrawn in the
 * same 20-unit / 1.4-stroke grammar. */

import type { ReactNode } from "react";

function Glyph({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 20 20"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

/** Compression — text lines squeezed by vertical arrows. */
const CompressionIcon = (
  <Glyph>
    <path d="M4.375 7.5H15.625" />
    <path d="M4.375 12.5H15.625" />
    <path d="M10 1.875V5" />
    <path d="M7.8125 3.4375L10 5.625L12.1875 3.4375" />
    <path d="M10 18.125V15" />
    <path d="M7.8125 16.5625L10 14.375L12.1875 16.5625" />
  </Glyph>
);

/** Cleanup — two sparkle crosses (design bitmap, redrawn). */
const CleanupIcon = (
  <Glyph>
    <path d="M7 3.5V9.5" strokeWidth="1.9" />
    <path d="M4 6.5H10" strokeWidth="1.9" />
    <path d="M13.75 10.75V16.25" strokeWidth="1.9" />
    <path d="M11 13.5H16.5" strokeWidth="1.9" />
  </Glyph>
);

/** Dedup — two stacked copies, the duplicate behind dashed away. Authored in
 *  the file's grammar; the design has no dedup card to trace. */
const DedupIcon = (
  <Glyph>
    <path d="M3.75 7.5H12.5V16.25H3.75V7.5Z" />
    <path d="M7.5 7.5V3.75H16.25V12.5H12.5" strokeDasharray="2 2.6" />
  </Glyph>
);

/** Truncation — solid lines cut to a dashed remainder. */
const TruncationIcon = (
  <Glyph>
    <path d="M3.75 6.25H10" />
    <path d="M3.75 10H10" />
    <path d="M3.75 13.75H10" />
    <path d="M11.25 3.125V16.875" strokeDasharray="2 2.6" />
    <path d="M13.125 6.25H16.25" strokeDasharray="2 2.6" />
    <path d="M13.125 10H15.3125" strokeDasharray="2 2.6" />
    <path d="M13.125 13.75H16.25" strokeDasharray="2 2.6" />
  </Glyph>
);

/** External offload — box with an outward arrow. */
const ExternalIcon = (
  <Glyph>
    <path d="M8.75 5.625H5.625V14.375H14.375V11.25" />
    <path d="M11.25 5.625H14.375V8.75" />
    <path d="M14.375 5.625L9.0625 10.9375" />
  </Glyph>
);

/** Shrinking — four arrows pointing inward (design bitmap, redrawn). */
const ShrinkingIcon = (
  <Glyph>
    <path d="M3.4 3.4L7.8 7.8" />
    <path d="M7.8 4.6V7.8H4.6" />
    <path d="M16.6 3.4L12.2 7.8" />
    <path d="M12.2 4.6V7.8H15.4" />
    <path d="M3.4 16.6L7.8 12.2" />
    <path d="M4.6 12.2H7.8V15.4" />
    <path d="M16.6 16.6L12.2 12.2" />
    <path d="M15.4 12.2H12.2V15.4" />
  </Glyph>
);

/** Slimming — rails squeezed by inward arrows. */
const SlimmingIcon = (
  <Glyph>
    <path d="M6.875 4.375V15.625" />
    <path d="M13.125 4.375V15.625" />
    <path d="M1.875 10H5" />
    <path d="M3.75 8.125L5.625 10L3.75 11.875" />
    <path d="M18.125 10H15" />
    <path d="M16.25 8.125L14.375 10L16.25 11.875" />
  </Glyph>
);

/** Routing — one source node fanning to two targets. */
const RoutingIcon = (
  <Glyph>
    <path d="M5 10H8.75" />
    <path d="M8.75 10C11.25 10 11.25 5.625 15 5.625" />
    <path d="M8.75 10C11.25 10 11.25 14.375 15 14.375" />
    <path d="M3.75 11.25C4.44036 11.25 5 10.6904 5 10C5 9.30964 4.44036 8.75 3.75 8.75C3.05964 8.75 2.5 9.30964 2.5 10C2.5 10.6904 3.05964 11.25 3.75 11.25Z" />
    <path d="M16.25 6.875C16.9404 6.875 17.5 6.31536 17.5 5.625C17.5 4.93464 16.9404 4.375 16.25 4.375C15.5596 4.375 15 4.93464 15 5.625C15 6.31536 15.5596 6.875 16.25 6.875Z" />
    <path d="M16.25 15.625C16.9404 15.625 17.5 15.0654 17.5 14.375C17.5 13.6846 16.9404 13.125 16.25 13.125C15.5596 13.125 15 13.6846 15 14.375C15 15.0654 15.5596 15.625 16.25 15.625Z" />
  </Glyph>
);

/** Pin — cache anchoring. */
const PinIcon = (
  <Glyph>
    <path d="M6.875 3.75H13.125" />
    <path d="M11.875 3.75L12.8125 8.75H7.1875L8.125 3.75" />
    <path d="M5.9375 8.75H14.0625" />
    <path d="M10 8.75V16.25" />
  </Glyph>
);

/** Script — document with text lines. */
const ScriptIcon = (
  <Glyph>
    <path d="M5.625 2.5H11.875L15 5.625V17.5H5.625V2.5Z" />
    <path d="M11.875 2.5V5.625H15" />
    <path d="M8.125 8.75H12.5" />
    <path d="M8.125 11.25H12.5" />
    <path d="M8.125 13.75H10.625" />
  </Glyph>
);

/** Cut — scissors. */
const CutIcon = (
  <Glyph>
    <path d="M4.375 6.875C5.41053 6.875 6.25 6.03553 6.25 5C6.25 3.96447 5.41053 3.125 4.375 3.125C3.33947 3.125 2.5 3.96447 2.5 5C2.5 6.03553 3.33947 6.875 4.375 6.875Z" />
    <path d="M4.375 16.875C5.41053 16.875 6.25 16.0355 6.25 15C6.25 13.9645 5.41053 13.125 4.375 13.125C3.33947 13.125 2.5 13.9645 2.5 15C2.5 16.0355 3.33947 16.875 4.375 16.875Z" />
    <path d="M5.75 6.1875L16.25 13.75" />
    <path d="M5.75 13.8125L16.25 6.25" />
  </Glyph>
);

/** Imaging — a framed picture, for the text-rendered-as-pixels optimizations. */
const ImageIcon = (
  <Glyph>
    <path d="M3.125 4.375H16.875V15.625H3.125V4.375Z" />
    <path d="M7.5 8.125C7.84518 8.125 8.125 7.84518 8.125 7.5C8.125 7.15482 7.84518 6.875 7.5 6.875C7.15482 6.875 6.875 7.15482 6.875 7.5C6.875 7.84518 7.15482 8.125 7.5 8.125Z" />
    <path d="M3.125 12.5L7.5 10L16.875 15.625" />
  </Glyph>
);

/* Icon per backend optimization id (MockAgentClient mirrors the BE list);
 * aliases cover design cards the backend may add later. */
const BY_ID: Record<string, ReactNode> = {
  tool_result_compression: CompressionIcon,
  tool_result_sanitization: CleanupIcon,
  tool_result_dedup: DedupIcon,
  tool_result_truncation: TruncationIcon,
  ccr: ExternalIcon,
  tool_definition_compression: ShrinkingIcon,
  tool_definition_slim: SlimmingIcon,
  routing: RoutingIcon,
  cache_anchor: PinIcon,
  bash_input_compression: ScriptIcon,
  skill_injection: CutIcon,
  pxpipe_prompt: ImageIcon,
  pxpipe_tool_results: ImageIcon,
};

/* Family fallback for ids the map doesn't know yet. */
const BY_FAMILY: Record<string, ReactNode> = {
  tool_results: CompressionIcon,
  tool_defs: ShrinkingIcon,
  routing_cache: RoutingIcon,
  prompt: CutIcon,
  stages: ScriptIcon,
};

export function OptimizationIcon({
  id,
  family,
}: {
  id: string;
  family: string;
}) {
  return <>{BY_ID[id] ?? BY_FAMILY[family] ?? CompressionIcon}</>;
}
